import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Copy, 
  Check, 
  Monitor, 
  Thermometer, 
  HardDrive,
  BookOpen,
  CheckCircle2
} from "lucide-react";
import SEO, { pageSEO } from "../components/SEO";
import MainLayout from "../components/MainLayout";

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
        copied 
          ? "bg-green-500/20 text-green-400 border border-green-500/30" 
          : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 border border-slate-600/50 hover:border-slate-500/50"
      }`}
      data-testid="copy-button"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          Copy Steps
        </>
      )}
    </button>
  );
};

// Tutorial Card Component
const TutorialCard = ({ icon: Icon, title, description, steps, stepText }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl border border-slate-700/50 overflow-hidden"
      data-testid={`tutorial-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-b border-slate-700/50 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <p className="text-slate-400 text-sm mt-1">{description}</p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="p-6">
        <div className="bg-slate-900/50 rounded-xl border border-slate-700/30 p-5">
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.title} className="flex gap-4">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                  <span className="text-blue-400 text-sm font-bold">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold text-sm">{step.title}</h4>
                  {step.details.map((detail, i) => (
                    <p key={`${step.title}-detail-${i}`} className="text-slate-400 text-sm mt-1">{detail}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Copy Button */}
        <div className="mt-4 flex justify-end">
          <CopyButton text={stepText} />
        </div>
      </div>
    </motion.div>
  );
};

const TutorialsPage = () => {
  const tutorials = [
    {
      icon: Monitor,
      title: "Basic PC Health Check",
      description: "A simple guide for users to check if their laptop is running properly.",
      steps: [
        {
          title: "Open Task Manager",
          details: [
            "Right-click the taskbar → Task Manager.",
            "If CPU or Disk stay above 90%, the laptop is overloaded."
          ]
        },
        {
          title: "Check storage",
          details: [
            "Settings → System → Storage.",
            "Delete temporary files if the drive is almost full."
          ]
        },
        {
          title: "Restart regularly",
          details: [
            "Helps clear memory and close stuck processes."
          ]
        },
        {
          title: "Update Windows",
          details: [
            "Settings → Windows Update → Check for updates."
          ]
        }
      ],
      stepText: `Basic PC Health Check

1. Open Task Manager:
   Right-click the taskbar → Task Manager.
   If CPU or Disk stay above 90%, the laptop is overloaded.

2. Check storage:
   Settings → System → Storage.
   Delete temporary files if the drive is almost full.

3. Restart regularly:
   Helps clear memory and close stuck processes.

4. Update Windows:
   Settings → Windows Update → Check for updates.`
    },
    {
      icon: Thermometer,
      title: "Laptop Overheating Check",
      description: "A quick guide for users with a hot or noisy laptop.",
      steps: [
        {
          title: "Clean the vents",
          details: [
            "Use a soft brush or cold air. Never use hot air."
          ]
        },
        {
          title: "Avoid soft surfaces",
          details: [
            "Do not use the laptop on a bed or sofa."
          ]
        },
        {
          title: "Close heavy apps",
          details: [
            "Browsers with many tabs, games, editing software."
          ]
        },
        {
          title: "Still overheating?",
          details: [
            "The laptop needs internal cleaning and new thermal paste."
          ]
        }
      ],
      stepText: `Laptop Overheating Check

1. Clean the vents:
   Use a soft brush or cold air. Never use hot air.

2. Avoid soft surfaces:
   Do not use the laptop on a bed or sofa.

3. Close heavy apps:
   Browsers with many tabs, games, editing software.

4. Still overheating?
   The laptop needs internal cleaning and new thermal paste.`
    },
    {
      icon: HardDrive,
      title: "Free Up Storage & Speed Boost",
      description: "A simple guide for users with a full or slow laptop.",
      steps: [
        {
          title: "Delete temporary files",
          details: [
            "Settings → Storage → Temporary files."
          ]
        },
        {
          title: "Uninstall unused apps",
          details: [
            "Settings → Apps → Uninstall."
          ]
        },
        {
          title: "Empty Recycle Bin",
          details: [
            "Many users forget this step."
          ]
        },
        {
          title: "Still no space?",
          details: [
            "An SSD upgrade gives more storage and much better speed."
          ]
        }
      ],
      stepText: `Free Up Storage & Speed Boost

1. Delete temporary files:
   Settings → Storage → Temporary files.

2. Uninstall unused apps:
   Settings → Apps → Uninstall.

3. Empty Recycle Bin:
   Many users forget this step.

4. Still no space?
   An SSD upgrade gives more storage and much better speed.`
    }
  ];

  return (
    <MainLayout>
      <div data-testid="tutorials-page">
        <SEO {...pageSEO.tutorials} />
        {/* Header */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white">
                    Simple PC Checks You Can Do at Home
                  </h1>
                </div>
              </div>
              
              <p className="text-slate-400 text-lg max-w-3xl mt-4">
                Quick and easy tutorials anyone can follow at home to check their laptop or PC. 
                These steps help users identify basic issues before needing professional repair.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Tutorials Grid */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid gap-8">
            {tutorials.map((tutorial) => (
              <TutorialCard
                key={tutorial.title}
                icon={tutorial.icon}
                title={tutorial.title}
                description={tutorial.description}
                steps={tutorial.steps}
                stepText={tutorial.stepText}
              />
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/20 p-8 text-center"
          >
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">
              Need Professional Help?
            </h3>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              If you've tried these steps and still have issues, our team can diagnose and fix your device quickly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/diagnostic"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity"
                data-testid="tutorials-diagnostic-btn"
              >
                <Monitor className="w-5 h-5" />
                Run Free Diagnostic
              </Link>
              <a
                href="https://wa.me/447435324947"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-green-600 transition-colors"
                data-testid="tutorials-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Contact on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TutorialsPage;
